import { NavLink, Outlet, useLocation } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useDTalkAccess } from '@/hooks/usePerformance';
import { cn } from '@/lib/utils';
import { Button } from '@/components/ui/button';
import { toDMY, todayAR } from '../../../../../shared/dates';

/**
 * Frame compartido de D-Talk (mockups docs/dtalk): barra de sub-tabs
 * Mi conversación | Mi equipo | Vista People | Histórico. Las tabs de líder /
 * People se muestran según el acceso resuelto (mismo roster que Mapa de Talento).
 */
export function DTalkLayout() {
  const { t } = useTranslation();
  const location = useLocation();
  const { data: access, error, refetch } = useDTalkAccess();
  // Error de plataforma (incidente 16/09: AccessDenied del backend): se muestra
  // como error con reintento, NUNCA como "ventana cerrada" con una fecha inventada.
  // Solo si NO hay datos usables: React Query conserva `data` cuando falla un
  // refetch en segundo plano; reemplazar la vista ahí desmontaría la conversación.
  if (error && !access) {
    return (
      <div className="mx-auto max-w-xl py-16 text-center">
        <p className="font-display text-2xl">{t('performance.dtalk.load_error.title')}</p>
        <p className="mt-3 text-sm text-muted-foreground">{t('performance.dtalk.load_error.body')}</p>
        <Button variant="outline" className="mt-6" onClick={() => { void refetch(); }}>
          {t('performance.dtalk.load_error.retry')}
        </Button>
      </div>
    );
  }

  const emailSeg = location.pathname.match(/\/performance\/dtalk\/(?:history)\/([^/]+)/)?.[1] ?? '';
  const histTo = emailSeg ? `history/${emailSeg}` : 'history';

  // Ventana cerrada: nadie conversa, pero lo ya hablado se sigue leyendo.
  //  - People (canViewAll) ve todo, como siempre, para preparar el ciclo.
  //  - Líder: su equipo, los informes y el histórico (Sol 20/10/2026: al cerrar
  //    la ventana los líderes se quedaban sin ver lo que habló su gente).
  //  - El resto: solo el aviso (antes de abrir, o ya cerrada).
  const closed = !!access && !access.windowOpen;
  const ended = !!access && closed && !!access.windowEnd && todayAR() > access.windowEnd;
  if (access && closed && !access.canViewAll && !access.isLeader) {
    return (
      <div className="mx-auto max-w-xl py-16 text-center">
        <p className="font-display text-2xl">{t(ended ? 'performance.dtalk.closed.ended_title' : 'performance.dtalk.closed.title')}</p>
        <p className="mt-3 text-sm text-muted-foreground">
          {ended
            ? t('performance.dtalk.closed.ended_body', { date: toDMY(access.windowEnd) })
            : t('performance.dtalk.closed.body', { date: toDMY(access.windowStart) })}
        </p>
      </div>
    );
  }

  const tabs: Array<{ to: string; base: string; label: string; show: boolean }> = [
    { to: '.', base: '', label: t('performance.dtalk.tabs.my'), show: true },
    { to: 'team', base: 'team', label: t('performance.dtalk.tabs.team'), show: !!access?.isLeader },
    { to: 'people', base: 'people', label: t('performance.dtalk.tabs.people'), show: !!access?.canViewAll },
    { to: histTo, base: 'history', label: t('performance.dtalk.tabs.history'), show: !!(access?.isLeader || access?.canViewAll) },
  ];

  return (
    <div className="max-w-7xl mx-auto p-4 sm:p-6 space-y-5">
      <div className="flex flex-wrap gap-2">
        {tabs.filter(x => x.show).map(tab => (
          <NavLink
            key={tab.base}
            to={tab.to}
            end={tab.base === ''}
            className={({ isActive }) => cn(
              'rounded-full px-5 py-2 text-sm font-semibold transition-colors',
              isActive
                ? 'bg-teal-600 text-white shadow-xs'
                : 'border border-border text-muted-foreground hover:text-foreground hover:bg-accent',
            )}
          >
            {tab.label}
          </NavLink>
        ))}
      </div>
      {closed && !access?.canViewAll && (
        <p className="rounded-lg border border-amber-200 bg-amber-50 px-4 py-2.5 text-sm text-amber-900 dark:border-amber-900/50 dark:bg-amber-950/30 dark:text-amber-200">
          {ended
            ? t('performance.dtalk.closed.leader_banner', { date: toDMY(access!.windowEnd) })
            : t('performance.dtalk.closed.leader_banner_before', { date: toDMY(access!.windowStart) })}
        </p>
      )}
      <Outlet />
    </div>
  );
}

/** Encabezado común de las vistas: título + breadcrumb "PEOPLE · D-TALK · {ciclo}". */
export function DTalkHeader({ title, cycleLabel }: { title: string; cycleLabel?: string }) {
  const { t } = useTranslation();
  return (
    <div>
      <h1 className="text-2xl font-bold tracking-tight">{title}</h1>
      <p className="text-xs font-semibold tracking-[0.15em] uppercase text-teal-600 mt-1">
        {t('performance.dtalk.section_label', { cycle: (cycleLabel || 'Ciclo 2026').toUpperCase() })}
      </p>
    </div>
  );
}

